<template>
  <Transition
    name="editor-side-panel-drawer"
    @before-enter="handleTransitionStart"
    @before-leave="handleTransitionStart"
    @after-enter="handleTransitionEnd"
    @after-leave="handleTransitionEnd"
    @enter-cancelled="handleTransitionEnd"
    @leave-cancelled="handleTransitionEnd"
  >
    <aside
      v-if="show"
      class="editor-side-panel-drawer"
      :class="{ 'editor-side-panel-drawer--resizing': resizing }"
      :style="drawerStyle"
    >
      <slot />

      <div
        class="editor-side-panel-drawer__resize-handle"
        role="separator"
        aria-orientation="vertical"
        :aria-label="resizeAriaLabel"
        @pointerdown="emit('resize-start', $event)"
      />
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount } from 'vue';
import type { CSSProperties } from 'vue';
import { useGlobalConfigStore } from '@/stores';

interface EditorSidePanelDrawerProps {
  /** 是否展示抽屉。 */
  show: boolean;
  /** 抽屉当前宽度，单位为像素。 */
  width: number;
  /** 是否处于拖拽调整宽度状态。 */
  resizing: boolean;
  /** 拖拽把手的无障碍文案。 */
  resizeAriaLabel?: string;
}

const props = withDefaults(defineProps<EditorSidePanelDrawerProps>(), {
  resizeAriaLabel: '调整左侧面板宽度',
});

const emit = defineEmits<{
  /** 通知外层页面开始处理宽度拖拽，避免抽屉组件直接持有业务状态。 */
  'resize-start': [event: PointerEvent];
}>();

let isTransitioning = false;
let isDisposed = false;
const transitionWaiters = new Set<() => void>();

/** 标记真实 Vue 过渡开始，不能以 DOM 更新完成替代动画完成。 */
function handleTransitionStart(): void {
  isTransitioning = true;
}

/** 通知所有等待方重新读取已稳定的抽屉布局。 */
function handleTransitionEnd(): void {
  isTransitioning = false;
  transitionWaiters.forEach((finish) => finish());
}

/**
 * 等待抽屉实际展开／收起完成；支持引导退出和组件销毁中止等待。
 * @param signal 调用方取消信号。
 * @returns 当前过渡结束后的通知。
 */
async function whenSettled(signal: AbortSignal): Promise<void> {
  await nextTick();
  if (signal.aborted || isDisposed) throw new DOMException('抽屉等待已取消', 'AbortError');
  while (isTransitioning) {
    await new Promise<void>((resolve, reject) => {
      /** 清理监听后结束等待，防止已离开的步骤继续持有组件。 */
      const finish = (): void => {
        transitionWaiters.delete(finish);
        signal.removeEventListener('abort', finish);
        if (signal.aborted || isDisposed) reject(new DOMException('抽屉等待已取消', 'AbortError'));
        else resolve();
      };
      transitionWaiters.add(finish);
      signal.addEventListener('abort', finish, { once: true });
    });
    // 取消当前过渡可能紧接着启动反向过渡，下一次 DOM 更新后再次确认稳定状态。
    await nextTick();
    if (signal.aborted || isDisposed) throw new DOMException('抽屉等待已取消', 'AbortError');
  }
}

defineExpose<EditorSidePanelDrawerHandle>({ whenSettled });
onBeforeUnmount(() => {
  isDisposed = true;
  handleTransitionEnd();
});

const globalConfigStore = useGlobalConfigStore();

type EditorSidePanelDrawerThemeVars = CSSProperties & Record<string, string>;

const isDarkTheme = computed(() => Boolean(globalConfigStore.getProviderTheme()));

const drawerThemeVars = computed<EditorSidePanelDrawerThemeVars>(() => {
  if (!isDarkTheme.value) {
    return {
      '--editor-side-panel-drawer-bg': '#ffffff',
      '--editor-side-panel-drawer-border': '#d7e0ec',
      '--editor-side-panel-divider': '#d7e0ec',
    };
  }

  return {
    '--editor-side-panel-drawer-bg': '#1a2028',
    '--editor-side-panel-drawer-border': '#303a47',
    '--editor-side-panel-divider': '#303a47',
  };
});

const drawerStyle = computed<CSSProperties>(() => ({
  width: `${props.width}px`,
  flexBasis: `${props.width}px`,
  ...drawerThemeVars.value,
}));
</script>

<style scoped>
.editor-side-panel-drawer {
  --editor-side-panel-drawer-bg: color-mix(in srgb, var(--n-card-color, var(--n-color)) 94%, var(--n-primary-color) 6%);
  --editor-side-panel-drawer-border: color-mix(in srgb, var(--n-border-color) 88%, var(--n-primary-color) 12%);
  --editor-side-panel-divider: var(--editor-side-panel-drawer-border);
  --editor-side-panel-header-padding: 14px 14px 12px;
  --editor-side-panel-header-gap: 10px;
  --editor-side-panel-title-size: 15px;
  --editor-side-panel-title-weight: 700;
  --editor-side-panel-title-line-height: 1.35;

  position: relative;
  height: 100%;
  flex: 0 0 auto;
  min-width: 0;
  overflow: hidden;
  border-right: 1px solid var(--editor-side-panel-drawer-border);
  background: var(--editor-side-panel-drawer-bg);
  transition:
    width 0.18s ease,
    flex-basis 0.18s ease,
    opacity 0.16s ease,
    transform 0.18s ease;
  will-change: width, flex-basis, opacity, transform;
}

.editor-side-panel-drawer--resizing {
  transition: none;
}

.editor-side-panel-drawer-enter-from,
.editor-side-panel-drawer-leave-to {
  width: 0 !important;
  flex-basis: 0 !important;
  opacity: 0;
  transform: translateX(-8px);
}

.editor-side-panel-drawer-enter-active,
.editor-side-panel-drawer-leave-active {
  overflow: hidden;
}

.editor-side-panel-drawer__resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
}

.editor-side-panel-drawer__resize-handle::after {
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  content: '';
  background-color: transparent;
  transition: background-color 0.16s ease;
}

.editor-side-panel-drawer__resize-handle:hover::after,
.editor-side-panel-drawer__resize-handle:active::after {
  background-color: var(--n-primary-color);
}
</style>
